import {
  useGetTagsQuery,
  useGetWorkspacesQuery,
  useLazyGetFolderQuery,
  useUpdateFolderTagsMutation,
} from '@carboncopyco/data';
import { notify } from '@carboncopyco/ui';
import { useSet } from '@uidotdev/usehooks';
import constate from 'constate';
import { isEqual } from 'lodash-es';
import { useEffect, useState } from 'react';

type FolderManageAccessContext = {
  folderId: string;
  workspaceId: string;
  organizationId: string;
  isOpen: boolean;
  onClose: () => void;
};

function useFolderManageAccessContext({
  organizationId,
  workspaceId,
  isOpen,
  onClose,
  folderId,
}: FolderManageAccessContext) {
  const [searchKey, setSearchKey] = useState<string>('');
  const [getFolder, { isLoading: isLoadingFolder, data: folder }] =
    useLazyGetFolderQuery();
  const [tagFolder, { isLoading: isSaving, isSuccess }] =
    useUpdateFolderTagsMutation();
  const selectedTags = useSet<string>();
  const selectedWorkspaces = useSet<string>();
  const { data: orgWorkspaces = [], isLoading: isLoadingWorkspaces } =
    useGetWorkspacesQuery({
      organizationId,
    });
  const workspaces = orgWorkspaces.filter(
    (ws) => String(ws.id) !== String(workspaceId)
  );
  const { data: tags = [], isLoading: isLoadingTags } = useGetTagsQuery({
    organizationId,
  });

  useEffect(() => {
    getFolder({
      organizationId,
      folderId,
      workspaceId,
    })
      .unwrap()
      .then((folder) => {
        folder.tags?.forEach((tag) => selectedTags.add(tag));
        folder.sharedWorkspaces?.forEach((workspace) =>
          selectedWorkspaces.add(workspace)
        );
      });
  }, [
    folderId,
    getFolder,
    organizationId,
    selectedTags,
    selectedWorkspaces,
    workspaceId,
  ]);

  const tagsList = searchKey
    ? tags?.filter(
        (tag) => tag.name.toLowerCase().indexOf(searchKey.toLowerCase()) !== -1
      )
    : tags;

  const workspacesList = searchKey
    ? workspaces?.filter(
        (ws) =>
          ws.workspaceName.toLowerCase().indexOf(searchKey.toLowerCase()) !== -1
      )
    : workspaces;

  const list = [...tagsList, ...workspacesList];

  const submit = () => {
    selectedWorkspaces.delete('');
    selectedTags.delete('');
    tagFolder({
      organizationId,
      workspaceId,
      folderId,
      sharedWorkspaces: [...selectedWorkspaces],
      tags: [...selectedTags],
    })
      .unwrap()
      .then(() => {
        notify.success('Folder settings updated successfully');
      })
      .catch(() => {
        notify.error('Failed to update folder settings');
      });
    onClose();
  };

  const clear = () => {
    tagsList?.forEach((tag) => selectedTags.delete(tag.id));
    workspacesList?.forEach((tag) => selectedWorkspaces.delete(tag.id));
  };

  const selectAll = () => {
    tagsList?.forEach((tag) => selectedTags.add(tag.id));
    workspacesList?.forEach((tag) => selectedWorkspaces.add(tag.id));
  };
  const isDirty =
    !isEqual(folder?.tags ?? [], [...selectedTags]) ||
    !isEqual(folder?.sharedWorkspaces ?? [], [...selectedWorkspaces]);

  const isLoading = isLoadingFolder || isLoadingWorkspaces || isLoadingTags;

  return {
    isLoading,
    isSuccess,
    isSaving,
    isDirty,
    list,
    selectedTags,
    selectedWorkspaces,
    searchKey,
    setSearchKey,
    workspaces,
    tags,
    folder,
    workspaceId,
    organizationId,
    isOpen,
    onClose,
    submit,
    clear,
    selectAll,
  };
}

export const [FolderManageAccessProvider, useFolderManageAccess] = constate(
  useFolderManageAccessContext
);
